import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogContentBase, DialogModule, DialogRef, KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger.service';
import { Discipline, EvaluationType } from '../../models/models.lib';
import { DisciplineService } from '../../discipline/discipline.service';
import { duplicateEvaluationValidator } from '../../common/utils.lib';

@Component({
  selector: 'casi-evaluation-add-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DialogModule,
    EditorModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    LayoutModule,
    AutoFocusDirective,
    KENDO_DIALOGS
  ],
  templateUrl: './evaluation-add-win.html',
  styleUrl: './evaluation-add-win.scss'
})
export class EvaluationAddWin implements OnInit {

  private _evaluation: EvaluationType | undefined;

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) existingData: EvaluationType[] = [];

  @Input({ required: true }) set evaluation(val: EvaluationType) {
    this._evaluation = val;
    this.editForm.patchValue(this._evaluation);
  }
  get evaluation(): EvaluationType | undefined {
    return this._evaluation;
  };

  private _mode: FormModes = FormModes.NOTSET;
  @Input({ required: true }) set mode(val: FormModes) {
    this._mode = val;
  }
  get mode(): FormModes {
    return this._mode;
  };

  @Output() onSubmit: EventEmitter<FormGroup> = new EventEmitter();
  @Output() onClose: EventEmitter<void> = new EventEmitter();

  editForm: FormGroup;
  disciplines: Discipline[] = [];
  formModes = FormModes;
  public value = '';

  constructor(
    private fb: FormBuilder,
    private logger: LoggerService,
    private dataService: DisciplineService
  ) {
    this.editForm = this.fb.group(
      {
        id: [null, []],
        evaluation_name: [null, [Validators.required]],
        discipline_id: [null],
        comment: [null],
        participant_instruction: [null, [Validators.required]],
        domain_instruction_student: [null, [Validators.required]],
        domain_instruction_professional: [null, [Validators.required]],
        domain_instruction_supervisor: [null, [Validators.required]],
      }
    );
  }

  //#region Form

  get f() { return this.editForm.controls; }
  get evaluation_name() { return this.editForm.get('evaluation_name'); }
  get discipline_id() { return this.editForm.get('discipline_id'); }
  get comment() { return this.editForm.get('comment'); }
  get participant_instruction() { return this.editForm.get('participant_instruction'); }
  get domain_instruction_student() { return this.editForm.get('domain_instruction_student'); }
  get domain_instruction_professional() { return this.editForm.get('domain_instruction_professional'); }
  get domain_instruction_supervisor() { return this.editForm.get('domain_instruction_supervisor'); }


  //#endregion

  //#region Ng handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    this.loadDisciplines();
    this.value = (this.evaluation?.participant_instruction as string);
    this.editForm.setValidators(
      duplicateEvaluationValidator(this.existingData, this.mode)
    );

    if (this.mode !== this.formModes.COPY) {
      this.editForm.get('discipline_id')?.setValidators([Validators.required]);
    }
    else {
      this.editForm.get('participant_instruction')?.clearValidators();
      this.editForm.get('domain_instruction_student')?.clearValidators();
      this.editForm.get('domain_instruction_professional')?.clearValidators();
      this.editForm.get('domain_instruction_supervisor')?.clearValidators();
      this.editForm.get('discipline_id')?.clearValidators();
    }
  }

  //#endregion

  //#region Public methods

  public valueChange(value: string): void {
    this.logger.debug(`valueChange...`, value);
    this.editForm.patchValue({ participant_instruction: value });
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
    // this.dialog.close({ text: CasiCloseOptions.Cancelled });
  }

  public onSaveAction(): void {
    this.logger.debug(`onSaveAction...`, this.editForm.value);
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.onSubmit.emit(this.editForm);
    // this.dialog.close({text: CasiCloseOptions.Saved, data: this.editForm.value});
  }

  markInvalidControls() {
    Object.keys(this.editForm.controls).forEach(key => {
      const control = this.editForm.get(key);
      if (control?.invalid) {
        console.log(`❌ ${key}`, control.errors);
      }
    });
  }

  //#endregion

  //#region Private methods

  private loadDisciplines(): void {
    this.dataService.get().subscribe({
      next: data => this.disciplines = data ? (this.mode == FormModes.NEW ? data.filter((d: Discipline) => d.active) : data) : [],
      error: err => console.error('Failed to load disciplines', err)
    });
  }
  //#endregion

}

